import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, saveToken, clearToken } from './api';
import { getLang } from './i18n';
import { getVariant, getAnonId } from './ab';

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        try {
            const { data } = await api.get('/auth/me');
            setUser(data);
        } catch {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        // If returning from Google OAuth callback, AuthCallback will handle session exchange first.
        if (window.location.hash?.includes('session_id=')) {
            setLoading(false);
            return;
        }
        checkAuth();
    }, [checkAuth]);

    const loginEmail = async (email, password) => {
        const { data } = await api.post('/auth/login', { email, password });
        saveToken(data.token);
        setUser(data.user);
        return data.user;
    };

    const registerEmail = async (email, password, alias) => {
        const { data } = await api.post('/auth/register', {
            email, password, alias,
            language: getLang(),
            ab_variant: getVariant() || undefined,
            anon_id: getAnonId() || undefined,
        });
        saveToken(data.token);
        setUser(data.user);
        return data.user;
    };

    const registerAnonymous = async () => {
        const { data } = await api.post('/auth/anonymous', {
            language: getLang(),
            ab_variant: getVariant() || undefined,
            anon_id: getAnonId() || undefined,
        });
        saveToken(data.token);
        setUser(data.user);
        return data.user;
    };

    const logout = async () => {
        try { await api.post('/auth/logout'); } catch {}
        clearToken();
        setUser(null);
    };

    const refresh = checkAuth;

    return (
        <AuthCtx.Provider value={{ user, loading, loginEmail, registerEmail, registerAnonymous, logout, setUser, refresh }}>
            {children}
        </AuthCtx.Provider>
    );
};

export const useAuth = () => useContext(AuthCtx);
